import React from 'react';
import { object } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import { StaticPage, TopbarContainer } from '..';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  MultiFilterSearchBar,
  SectionUsefulGuides,
} from '../../components';
import { getStateHireSearchLinks } from '../../util/stateHireSearchLinks';
import { stateLandingPages } from '../../util/stateLandingPages';
import {
  getTrailerHirePageTypeCards,
  getTrailerHirePageGuideLinks,
} from '../../util/trailerHirePageBrowseLinks';
import SectionTrailerTypeCards from '../../components/SectionSuburbTrailerTypeCards/SectionTrailerTypeCards';
import PopularLocationsSection from '../AllLocationsPage/PopularLocationsSection';

import css from './TrailerHirePage.module.css';

const stateLinkLabel = (intl, link) =>
  intl.formatMessage(
    {
      id:
        link.linkPrep === 'inThe'
          ? 'TrailerHirePage.stateLinkInThe'
          : 'TrailerHirePage.stateLinkIn',
    },
    { location: link.linkLocation }
  );

const stateLandingByCode = Object.values(stateLandingPages).reduce((acc, page) => {
  acc[page.stateCode] = page;
  return acc;
}, {});

const StateDirectoryLink = ({ link, intl }) => {
  const label = stateLinkLabel(intl, link);
  const landing = stateLandingByCode[link.stateCode];

  if (landing) {
    return (
      <NamedLink
        name="StateLandingPage"
        params={{ stateSlug: landing.slug }}
        className={css.linkCard}
      >
        {label}
      </NamedLink>
    );
  }

  return (
    <NamedLink name="SearchPage" to={{ search: link.searchUrl }} className={css.linkCard}>
      {label}
    </NamedLink>
  );
};

export const TrailerHirePageComponent = ({ history, intl }) => {
  const { siteTitle } = config;
  const stateLinks = getStateHireSearchLinks();
  const trailerTypeCards = getTrailerHirePageTypeCards(intl);
  const guideLinks = getTrailerHirePageGuideLinks(intl);

  const schemaTitle = intl.formatMessage({ id: 'TrailerHirePage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'TrailerHirePage.schemaDescription' },
    { siteTitle }
  );

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: schemaTitle,
    description: schemaDescription,
    about: {
      '@type': 'Organization',
      name: siteTitle,
      description: schemaDescription,
    },
    hasPart: [
      {
        '@type': 'ItemList',
        name: intl.formatMessage({ id: 'TrailerHirePage.statesHeading' }),
        numberOfItems: Object.keys(stateLandingByCode).length,
        itemListElement: stateLinks
          .filter(link => stateLandingByCode[link.stateCode])
          .map((link, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: stateLinkLabel(intl, link),
            url: `/trailer-hire/${stateLandingByCode[link.stateCode].slug}`,
          })),
      },
      {
        '@type': 'ItemList',
        name: intl.formatMessage({ id: 'TrailerHirePage.trailerTypesHeading' }),
        numberOfItems: trailerTypeCards.length,
        itemListElement: trailerTypeCards.map((card, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: card.title,
          description: card.description,
          url: card.href || `/s${card.searchUrl}`,
        })),
      },
    ],
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: intl.formatMessage({ id: 'TrailerHirePage.breadcrumbHome' }),
        item: `${config.canonicalRootURL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: intl.formatMessage({ id: 'TrailerHirePage.breadcrumbTrailerHire' }),
        item: `${config.canonicalRootURL}/trailer-hire`,
      },
    ],
  };

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={[schema, breadcrumbSchema]}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.root}>
          <div className={css.hero}>
            <nav
              className={css.breadcrumbs}
              aria-label={intl.formatMessage({ id: 'TrailerHirePage.breadcrumbNavLabel' })}
            >
              <ol className={css.breadcrumbList}>
                <li className={css.breadcrumbItem}>
                  <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                    <FormattedMessage id="TrailerHirePage.breadcrumbHome" />
                  </NamedLink>
                </li>
                <li className={css.breadcrumbSeparatorItem} aria-hidden="true">
                  <span className={css.breadcrumbSeparator} />
                </li>
                <li className={css.breadcrumbItem}>
                  <span className={css.breadcrumbCurrent} aria-current="page">
                    <FormattedMessage id="TrailerHirePage.breadcrumbTrailerHire" />
                  </span>
                </li>
              </ol>
            </nav>
            <h1 className={css.pageTitle}>
              <FormattedMessage id="TrailerHirePage.title" />
            </h1>
            <p className={css.intro}>
              <FormattedMessage id="TrailerHirePage.intro" values={{ siteTitle }} />
            </p>

            <section
              className={css.sectionSearch}
              aria-label={intl.formatMessage({ id: 'TrailerHirePage.searchAriaLabel' })}
            >
              <h2 className={css.searchHeading}>
                <FormattedMessage id="TrailerHirePage.searchHeading" />
              </h2>
              <p className={css.searchIntro}>
                <FormattedMessage id="TrailerHirePage.searchIntro" />
              </p>
              <div className={css.searchBar}>
                <MultiFilterSearchBar history={history} />
              </div>
            </section>
          </div>

          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <section aria-labelledby="trailer-hire-across-australia">
                  <h2 className={css.sectionHeading} id="trailer-hire-across-australia">
                    <FormattedMessage id="TrailerHirePage.acrossAustraliaHeading" />
                  </h2>
                  <p className={css.body}>
                    <FormattedMessage
                      id="TrailerHirePage.acrossAustraliaBody"
                      values={{ siteTitle }}
                    />
                  </p>
                </section>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <nav
                  aria-labelledby="trailer-hire-states"
                  aria-label={intl.formatMessage({ id: 'TrailerHirePage.statesNavLabel' })}
                >
                  <h2 className={css.sectionHeading} id="trailer-hire-states">
                    <FormattedMessage id="TrailerHirePage.statesHeading" />
                  </h2>
                  <p className={css.body}>
                    <FormattedMessage id="TrailerHirePage.statesIntro" />
                  </p>
                  <ul className={css.linkCardList}>
                    {stateLinks.map(link => (
                      <li key={link.stateCode} className={css.linkCardItem}>
                        <StateDirectoryLink link={link} intl={intl} />
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <PopularLocationsSection
                  intl={intl}
                  locationSet="primary"
                  headingMessageId="TrailerHirePage.majorCitiesHeading"
                  headingId="trailer-hire-major-cities"
                />
                <p className={css.viewAllLocations}>
                  <NamedLink name="AllLocationsPage" className={css.inlineLink}>
                    <FormattedMessage id="TrailerHirePage.viewAllLocations" />
                  </NamedLink>
                </p>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionTrailerTypeCards
                  headingId="trailer-hire-types"
                  heading={<FormattedMessage id="TrailerHirePage.trailerTypesHeading" />}
                  intro={<FormattedMessage id="TrailerHirePage.trailerTypesIntro" />}
                  layout="responsiveGrid"
                  cards={trailerTypeCards}
                />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <section aria-labelledby="trailer-hire-how-it-works">
                  <h2 className={css.sectionHeading} id="trailer-hire-how-it-works">
                    <FormattedMessage id="TrailerHirePage.howItWorksHeading" />
                  </h2>
                  <ol className={css.stepsList}>
                    <li>
                      <FormattedMessage id="TrailerHirePage.howItWorks.step1" />
                    </li>
                    <li>
                      <FormattedMessage id="TrailerHirePage.howItWorks.step2" />
                    </li>
                    <li>
                      <FormattedMessage id="TrailerHirePage.howItWorks.step3" />
                    </li>
                    <li>
                      <FormattedMessage id="TrailerHirePage.howItWorks.step4" />
                    </li>
                  </ol>
                  <p className={css.body}>
                    <NamedLink name="ForHirersPage" className={css.inlineLink}>
                      <FormattedMessage id="TrailerHirePage.howItWorksMore" />
                    </NamedLink>
                  </p>
                </section>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionUsefulGuides
                  headingId="trailer-hire-guides"
                  headingMessageId="TrailerHirePage.guidesHeading"
                  introMessageId="TrailerHirePage.guidesIntro"
                  guides={guideLinks}
                  showAdviceHubLink
                />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

TrailerHirePageComponent.propTypes = {
  history: object.isRequired,
  intl: intlShape.isRequired,
};

const TrailerHirePage = compose(
  withRouter,
  injectIntl
)(TrailerHirePageComponent);

export default TrailerHirePage;
